import getAnonymousSessionId from './anonymous-session';

const DEFAULT_DEV_API_ORIGIN = 'http://127.0.0.1:8000';
const DEFAULT_REQUEST_TIMEOUT_MS = 15000;
const DEFAULT_UPLOAD_TIMEOUT_MS = 120000;

const getApiOrigin = () => {
    if (typeof window !== 'undefined' && window.SCRATCH_CLOUD_API_ORIGIN) {
        return window.SCRATCH_CLOUD_API_ORIGIN;
    }
    if (typeof window !== 'undefined' && window.location) {
        const {hostname, origin, port, protocol} = window.location;
        const isLocalDevHost = hostname === '127.0.0.1' || hostname === 'localhost';
        const isDevServerPort = port === '8601';
        if (isLocalDevHost && isDevServerPort) {
            return DEFAULT_DEV_API_ORIGIN;
        }
        if (protocol === 'http:' || protocol === 'https:') {
            return origin;
        }
    }
    return DEFAULT_DEV_API_ORIGIN;
};

const getApiBase = () => `${getApiOrigin().replace(/\/$/, '')}/api`;
const getAnonymousSessionHeaders = () => {
    const anonymousSessionId = getAnonymousSessionId();
    return anonymousSessionId ? {
        'X-Anonymous-Session-Id': anonymousSessionId
    } : {};
};
const getEditTokenHeaders = editToken => ({
    ...getAnonymousSessionHeaders(),
    ...(editToken ? {
        'X-Project-Edit-Token': editToken
    } : {})
});

const fetchWithTimeout = async (url, options = {}, timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS) => {
    const controller = new AbortController();
    const timeoutId = globalThis.setTimeout(() => controller.abort(), timeoutMs);
    try {
        return await fetch(url, {
            ...options,
            signal: options.signal || controller.signal
        });
    } catch (error) {
        if (error && error.name === 'AbortError') {
            throw new Error('Request timed out, please retry');
        }
        throw error;
    } finally {
        globalThis.clearTimeout(timeoutId);
    }
};

const requestJson = async (path, options = {}) => {
    const {headers, ...restOptions} = options;
    const response = await fetchWithTimeout(`${getApiBase()}${path}`, {
        ...restOptions,
        headers: {
            'Content-Type': 'application/json',
            ...getAnonymousSessionHeaders(),
            ...(headers || {})
        }
    });
    if (!response.ok) {
        let message = `Request failed with status ${response.status}`;
        try {
            const data = await response.json();
            if (data && data.detail) {
                message = data.detail;
            }
        } catch (e) {
            // Keep the fallback message when the error body is not JSON.
        }
        throw new Error(message);
    }
    return response.json();
};

export const createCloudProject = payload => requestJson('/projects', {
    method: 'POST',
    body: JSON.stringify(payload)
});

export const bootstrapCloudProjectUpload = payload => requestJson('/projects/bootstrap-upload', {
    method: 'POST',
    body: JSON.stringify(payload)
});

export const requestUploadToken = (projectId, payload, editToken) => requestJson(`/projects/${projectId}/upload-token`, {
    method: 'POST',
    headers: getEditTokenHeaders(editToken),
    body: JSON.stringify(payload || {})
});

export const saveCloudProject = (projectId, payload, editToken) => requestJson(`/projects/${projectId}/save`, {
    method: 'POST',
    headers: getEditTokenHeaders(editToken),
    body: JSON.stringify(payload)
});

export const publishCloudProject = (projectId, editToken) => requestJson(`/projects/${projectId}/publish`, {
    method: 'POST',
    headers: getEditTokenHeaders(editToken)
});

export const getSharedProject = slug => requestJson(`/share/${slug}`);

const regionUploadHosts = {
    z0: 'https://upload.qiniup.com',
    z1: 'https://upload-z1.qiniup.com',
    z2: 'https://upload-z2.qiniup.com',
    na0: 'https://upload-na0.qiniup.com',
    as0: 'https://upload-as0.qiniup.com'
};

export const uploadBlobToQiniu = async ({token, key, file, region}) => {
    const uploadHost = regionUploadHosts[region] || regionUploadHosts.z2;
    const formData = new FormData();
    formData.append('token', token);
    formData.append('key', key);
    formData.append('file', file, `${key.split('/').pop() || 'project'}.sb3`);

    const response = await fetchWithTimeout(uploadHost, {
        method: 'POST',
        body: formData
    }, DEFAULT_UPLOAD_TIMEOUT_MS);
    if (!response.ok) {
        let message = `Qiniu upload failed with status ${response.status}`;
        try {
            const data = await response.json();
            if (data && data.error) {
                message = data.error;
            }
        } catch (e) {
            // Keep the fallback message when the error body is not JSON.
        }
        throw new Error(message);
    }
    return response.json();
};
